import React, { useState, useEffect } from "react";
import axios from "axios";
import { Helmet } from "react-helmet";
import { useParams, useLocation } from "react-router-dom";

function PushManually({ chimeraserv, user }) {
  const location = useLocation();
  const { id } = useParams();
  const integration = location.state.data;

  const [groups, setGroups] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState("");
  const [endDate, setEndDate] = useState("");
  const [weekInfo, setWeekInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState("");

  const [bearerToken, setBearerToken] = useState(null);
  const [tokenExpiry, setTokenExpiry] = useState(null);

  async function getBearerToken() {
    // Check if token exists and is still valid (with 5 minute buffer)
    if (
      bearerToken &&
      tokenExpiry &&
      Date.now() < tokenExpiry - 5 * 60 * 1000
    ) {
      console.log("Using existing bearer token");
      return bearerToken;
    }

    try {
      // Call your backend endpoint instead of Microsoft directly
      const tokenResponse = await axios({
        method: "POST",
        url: `${chimeraserv.urlGet}/api/subway/auth/token`, // Your backend endpoint
        headers: {
          Authorization: user.data.usrKey,
          "Content-Type": "application/json",
        },
        timeout: 10000,
      });

      const newToken = tokenResponse.data.access_token;
      const newExpiry =
        Date.now() + (tokenResponse.data.expires_in || 3600) * 1000;

      setBearerToken(newToken);
      setTokenExpiry(newExpiry);

      console.log("Bearer token fetched successfully");
      return newToken;
    } catch (err) {
      console.error(
        "Error fetching bearer token:",
        err.response?.data || err.message
      );
      throw new Error(`Failed to get bearer token: ${err.message}`);
    }
  }

  // Fetch group details and set stores from matching brand
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await axios.get(chimeraserv.urlGet + "/group", {
          headers: {
            Authorization: user.data.usrKey,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });
        setGroups(res.data);
        // Find brand by braKey
        let brandOutlets = [];
        for (const group of res.data) {
          const brand = (group.brands || []).find(
            (b) => b.braKey === integration.braKey
          );
          if (brand && brand.chains) {
            brandOutlets = brand.chains;
            break;
          }
        }
        setStores(brandOutlets);
      } catch (err) {
        setGroups([]);
        setStores([]);
      }
    };
    fetchGroups();
  }, [chimeraserv.urlGet, user.data.usrKey, integration.braKey]);

  // Only allow Wednesdays for endDate
  const handleEndDateChange = (e) => {
    const value = e.target.value;
    const date = new Date(value);
    if (date.getDay() !== 3) {
      setEndDate("");
      setWeekInfo("");
      setError("Please select a Wednesday!");
      return;
    }
    setEndDate(date.getTime()); // epoch ms local time
    setError("");
    // Show week range
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() - 7);
    const weekEnd = new Date(date - 24 * 60 * 60 * 1000);
    const format = (d) => d.toISOString().slice(0, 10);
    setWeekInfo(
      `Data Pushing for week : ${format(weekStart)} to ${format(weekEnd)}`
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResponse(null);
    setError("");

    try {
      // Find the selected outlet object
      const selectedOutletObj = stores.find((o) => o.chaKey === selectedOutlet);

      // Validate outlet selection
      if (!selectedOutletObj) {
        throw new Error("Selected outlet not found");
      }

      // Default storeNum from outlet object
      let storeNum = selectedOutletObj?.storeNum || "";

      // If selected outlet matches integration outlet, use integration storeNum
      for (const outlet of integration.outlets) {
        if (outlet.chaKey === selectedOutletObj.chaKey) {
          storeNum = outlet.storeNum;
          break;
        }
      }

      console.log("Fetching sales data...");
      const fetchResponse = await axios({
        method: "POST",
        url: chimeraserv.urlGet + `/api/subway/weekly-sales/${selectedOutlet}`,
        headers: {
          Authorization:
            user.data.usrKey ||
            "eyJ1c2VybmFtZSI6ImNoc0FkbWluIiwicGFzc3dvcmQiOiJhZG1pbiJ9",
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: {
          endDate: Math.floor(endDate / 1000),
          storeNum,
          vendorId: integration.vendorID,
          vendorName: integration.vendorName,
          vendorCountry: integration.vendorCountry,
        },
        timeout: 30000,
      });

      const dataToPush = fetchResponse.data;
      console.log("Sales data fetched successfully", dataToPush);

      const pushResponse = await axios({
        method: "POST",
        url: `${chimeraserv.urlGet}/api/subway/push-sales`, // Your backend endpoint
        headers: {
          Authorization: user.data.usrKey,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: {
          salesData: dataToPush,
          outletInfo: {
            chaKey: selectedOutletObj.chaKey,
            name: selectedOutletObj.name,
            storeNum: storeNum,
          },
        },
        timeout: 60000, // 60 seconds for push operation
      });

      console.log(
        `Push successful for ${selectedOutletObj.name} - Status: ${pushResponse.status}`
      );

      setResponse({
        status: "success",
        message: "Data pushed successfully",
        outlet: selectedOutletObj.name,
        data: dataToPush,
        response: pushResponse.data,
      });
    } catch (err) {
      console.error("Error in handleSubmit:", err);

      if (err.response?.status === 404) {
        setError(
          "Backend endpoint not found. Please check your backend configuration."
        );
      } else if (err.response?.status === 401) {
        setError("Authentication failed. Please check your credentials.");
      } else if (err.response?.status === 500) {
        setError("Server error occurred while pushing data.");
      } else if (err.code === "ECONNABORTED") {
        setError("Request timed out. Please try again.");
      } else {
        setError(err.message || "Failed to push weekly sales data.");
      }
    }

    setLoading(false);
  };

  return (
    <div className="custForm">
      <Helmet>
        <title>Re-push Weekly Sales | Chimera Solutions | Insights</title>
      </Helmet>
      <div className="viewTitle">Re-push Weekly Sales</div>
      <form className="form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Outlet:</label>
          <select
            className="form-control"
            value={selectedOutlet}
            onChange={(e) => setSelectedOutlet(e.target.value)}
            required
          >
            <option value="">Select Outlet</option>
            {stores.map((outlet) => (
              <option value={outlet.chaKey} key={outlet.chaKey}>
                {outlet.name}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>End Date (Wednesday only):</label>
          <input
            type="date"
            className="form-control"
            value={endDate ? new Date(endDate).toISOString().slice(0, 10) : ""}
            onChange={handleEndDateChange}
            required
          />
        </div>
        {weekInfo && <div className="form-group week-info">{weekInfo}</div>}
        {error && (
          <div className="form-group" style={{ color: "#c0392b" }}>
            {error}
          </div>
        )}
        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!selectedOutlet || !endDate || loading}
          >
            {loading ? "Pushing..." : "Push"}
          </button>
        </div>
      </form>
      {response && (
        <div
          className="api-response"
          style={{
            margin: "2em 0",
            background: "#f8f8f8",
            padding: "1em",
            borderRadius: "6px",
            fontSize: "0.95em",
          }}
        >
          <strong>API Response:</strong>
          <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {JSON.stringify(response, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

export default PushManually;
